import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useJobs } from '../contexts/JobContext';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Textarea } from '../components/Textarea';
import { JobStatus, JobCost } from '../types';
import { ArrowLeftIcon, CalendarIcon, DollarSignIcon, PlusIcon, XIcon, MapPinIcon, UserIcon, PhoneIcon, AlertCircleIcon, ImageIcon } from 'lucide-react';
export function AdminJobDetails() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    getJobById,
    updateJob
  } = useJobs();
  const job = id ? getJobById(id) : undefined;
  const [showSchedule, setShowSchedule] = useState(false);
  const [showAddCost, setShowAddCost] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(job?.scheduledDate || '');
  const [revenue, setRevenue] = useState(job?.revenue?.toString() || '');
  const [notes, setNotes] = useState(job?.notes || '');
  const [newCost, setNewCost] = useState({
    category: 'fuel' as JobCost['category'],
    amount: '',
    description: ''
  });
  if (!job) {
    return <div className="p-6" data-id="element-307">Job not found</div>;
  }
  const totalCosts = job.costs.reduce((sum, c) => sum + c.amount, 0);
  const profit = (job.revenue || 0) - totalCosts;
  const handleStatusChange = (status: JobStatus) => {
    updateJob(job.id, {
      status
    });
  };
  const handleSchedule = () => {
    updateJob(job.id, {
      scheduledDate,
      status: 'scheduled'
    });
    setShowSchedule(false);
  };
  const handleAddCost = () => {
    const cost: JobCost = {
      id: Date.now().toString(),
      category: newCost.category,
      amount: parseFloat(newCost.amount),
      description: newCost.description,
      date: new Date().toISOString()
    };
    updateJob(job.id, {
      costs: [...job.costs, cost]
    });
    setNewCost({
      category: 'fuel',
      amount: '',
      description: ''
    });
    setShowAddCost(false);
  };
  const handleRemoveCost = (costId: string) => {
    updateJob(job.id, {
      costs: job.costs.filter(c => c.id !== costId)
    });
  };
  const handleSaveRevenue = () => {
    updateJob(job.id, {
      revenue: parseFloat(revenue) || 0
    });
  };
  const handleSaveNotes = () => {
    updateJob(job.id, {
      notes
    });
  };
  const statusOptions: JobStatus[] = ['requested', 'scheduled', 'in-progress', 'complete', 'paid'];
  return <div className="min-h-screen bg-slate-50 pb-6" data-id="element-308">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6 sticky top-0 z-10" data-id="element-309">
        <div className="flex items-center gap-4" data-id="element-310">
          <button onClick={() => navigate('/admin')} className="p-2 hover:bg-slate-700 rounded-lg transition-colors" data-id="element-311">
            <ArrowLeftIcon className="w-6 h-6" data-id="element-312" />
          </button>
          <div className="flex-1" data-id="element-313">
            <h1 className="text-xl font-bold mb-1" data-id="element-314">{job.title}</h1>
            <p className="text-slate-300 text-sm" data-id="element-315">Job #{job.id}</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6" data-id="element-316">
        {/* Customer Info */}
        <div className="bg-white rounded-xl shadow-md p-6" data-id="element-317">
          <h3 className="font-bold text-slate-900 mb-4" data-id="element-318">
            Customer Information
          </h3>
          <div className="space-y-3" data-id="element-319">
            <div className="flex items-center gap-3 text-slate-700" data-id="element-320">
              <UserIcon className="w-5 h-5 text-slate-400" data-id="element-321" />
              <span data-id="element-322">{job.customerName}</span>
            </div>
            <div className="flex items-center gap-3 text-slate-700" data-id="element-323">
              <MapPinIcon className="w-5 h-5 text-slate-400" data-id="element-324" />
              <span data-id="element-325">{job.location}</span>
            </div>
            <div className="flex items-center gap-3" data-id="element-326">
              <AlertCircleIcon className="w-5 h-5 text-slate-400" data-id="element-327" />
              <span className={`font-semibold capitalize ${job.urgency === 'emergency' ? 'text-red-600' : job.urgency === 'urgent' ? 'text-orange-600' : 'text-slate-600'}`} data-id="element-328">
                {job.urgency} Priority
              </span>
            </div>
          </div>
        </div>

        {/* Job Details */}
        <div className="bg-white rounded-xl shadow-md p-6" data-id="element-329">
          <h3 className="font-bold text-slate-900 mb-4" data-id="element-330">Job Details</h3>
          <div className="space-y-3" data-id="element-331">
            <div data-id="element-332">
              <p className="text-sm font-semibold text-slate-500 mb-1" data-id="element-333">Type</p>
              <p className="text-slate-900 capitalize" data-id="element-334">{job.type}</p>
            </div>
            <div data-id="element-335">
              <p className="text-sm font-semibold text-slate-500 mb-1" data-id="element-336">
                Description
              </p>
              <p className="text-slate-900" data-id="element-337">{job.description}</p>
            </div>
            {job.media.length > 0 && <div data-id="element-338">
                <p className="text-sm font-semibold text-slate-500 mb-2" data-id="element-339">
                  Media ({job.media.length})
                </p>
                <div className="grid grid-cols-3 gap-2" data-id="element-340">
                  {job.media.map(m => <div key={m.id} className="aspect-square bg-slate-200 rounded-lg overflow-hidden" data-id="element-341">
                      <img src={m.url} alt="" className="w-full h-full object-cover" data-id="element-342" />
                    </div>)}
                </div>
              </div>}
          </div>
        </div>

        {/* Status Management */}
        <div className="bg-white rounded-xl shadow-md p-6" data-id="element-343">
          <h3 className="font-bold text-slate-900 mb-4" data-id="element-344">Status</h3>
          <div className="grid grid-cols-2 gap-2" data-id="element-345">
            {statusOptions.map(status => <button key={status} onClick={() => handleStatusChange(status)} className={`p-3 rounded-lg font-semibold text-sm capitalize transition-all ${job.status === status ? 'bg-orange-600 text-white shadow-lg' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-346">
                {status === 'in-progress' ? 'In Progress' : status}
              </button>)}
          </div>
        </div>

        {/* Scheduling */}
        <div className="bg-white rounded-xl shadow-md p-6" data-id="element-347">
          <div className="flex items-center justify-between mb-4" data-id="element-348">
            <h3 className="font-bold text-slate-900" data-id="element-349">Schedule</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowSchedule(!showSchedule)} data-id="element-350">
              <CalendarIcon className="w-4 h-4 mr-2" data-id="element-351" />
              {job.scheduledDate ? 'Reschedule' : 'Schedule'}
            </Button>
          </div>

          {job.scheduledDate && !showSchedule && <p className="text-slate-700" data-id="element-352">
              Scheduled for: {new Date(job.scheduledDate).toLocaleString()}
            </p>}

          {showSchedule && <div className="space-y-3" data-id="element-353">
              <Input type="datetime-local" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)} data-id="element-354" />
              <div className="flex gap-2" data-id="element-355">
                <Button variant="primary" size="sm" onClick={handleSchedule} data-id="element-356">
                  Save Schedule
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowSchedule(false)} data-id="element-357">
                  Cancel
                </Button>
              </div>
            </div>}
        </div>

        {/* Cost Tracking */}
        <div className="bg-white rounded-xl shadow-md p-6" data-id="element-358">
          <div className="flex items-center justify-between mb-4" data-id="element-359">
            <h3 className="font-bold text-slate-900" data-id="element-360">Costs</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowAddCost(!showAddCost)} data-id="element-361">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-362" />
              Add Cost
            </Button>
          </div>

          {showAddCost && <div className="mb-4 p-4 bg-slate-50 rounded-lg space-y-3" data-id="element-363">
              <select value={newCost.category} onChange={e => setNewCost({
            ...newCost,
            category: e.target.value as JobCost['category']
          })} className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:border-orange-500" data-id="element-364">
                <option value="fuel" data-id="element-365">Fuel</option>
                <option value="media" data-id="element-366">Media</option>
                <option value="labor" data-id="element-367">Labor</option>
                <option value="other" data-id="element-368">Other</option>
              </select>
              <Input type="number" placeholder="Amount" value={newCost.amount} onChange={e => setNewCost({
            ...newCost,
            amount: e.target.value
          })} data-id="element-369" />
              <Input placeholder="Description" value={newCost.description} onChange={e => setNewCost({
            ...newCost,
            description: e.target.value
          })} data-id="element-370" />
              <div className="flex gap-2" data-id="element-371">
                <Button variant="primary" size="sm" onClick={handleAddCost} data-id="element-372">
                  Add
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowAddCost(false)} data-id="element-373">
                  Cancel
                </Button>
              </div>
            </div>}

          <div className="space-y-2" data-id="element-374">
            {job.costs.map(cost => <div key={cost.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-id="element-375">
                <div className="flex-1" data-id="element-376">
                  <p className="font-semibold text-slate-900 capitalize" data-id="element-377">
                    {cost.category}
                  </p>
                  <p className="text-sm text-slate-500" data-id="element-378">{cost.description}</p>
                </div>
                <div className="flex items-center gap-3" data-id="element-379">
                  <p className="font-bold text-slate-900" data-id="element-380">${cost.amount}</p>
                  <button onClick={() => handleRemoveCost(cost.id)} className="p-1 text-red-600 hover:bg-red-50 rounded" data-id="element-381">
                    <XIcon className="w-4 h-4" data-id="element-382" />
                  </button>
                </div>
              </div>)}
            {job.costs.length === 0 && <p className="text-slate-500 text-sm" data-id="element-383">No costs added yet</p>}
          </div>

          <div className="mt-4 pt-4 border-t-2 border-slate-100" data-id="element-384">
            <div className="flex items-center justify-between font-bold text-lg" data-id="element-385">
              <span className="text-slate-900" data-id="element-386">Total Costs</span>
              <span className="text-slate-900" data-id="element-387">${totalCosts.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Revenue & Profit */}
        <div className="bg-white rounded-xl shadow-md p-6" data-id="element-388">
          <h3 className="font-bold text-slate-900 mb-4" data-id="element-389">Revenue & Profit</h3>
          <div className="space-y-4" data-id="element-390">
            <div className="flex items-end gap-3" data-id="element-391">
              <Input label="Job Revenue" type="number" placeholder="0.00" value={revenue} onChange={e => setRevenue(e.target.value)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-393" />} data-id="element-392" />
              <Button variant="primary" size="md" onClick={handleSaveRevenue} data-id="element-394">
                Save
              </Button>
            </div>

            <div className="p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg" data-id="element-395">
              <p className="text-sm font-semibold text-orange-900 mb-2" data-id="element-396">
                Net Profit
              </p>
              <p className={`text-3xl font-bold ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-397">
                ${profit.toFixed(2)}
              </p>
              <div className="mt-2 flex items-center gap-4 text-xs text-orange-800" data-id="element-398">
                <span data-id="element-399">Revenue: ${(job.revenue || 0).toFixed(2)}</span>
                <span data-id="element-400">Costs: ${totalCosts.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-xl shadow-md p-6" data-id="element-401">
          <h3 className="font-bold text-slate-900 mb-4" data-id="element-402">Admin Notes</h3>
          <Textarea placeholder="Add internal notes about this job..." value={notes} onChange={e => setNotes(e.target.value)} rows={4} data-id="element-403" />
          <Button variant="primary" size="sm" onClick={handleSaveNotes} className="mt-3" data-id="element-404">
            Save Notes
          </Button>
        </div>
      </div>
    </div>;
}